import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changeRecoveryRolloutMode,
  fetchRecoveryControlCenterMismatches,
  fetchRecoveryControlCenterOverview,
  fetchRecoveryControlCenterPerformance,
  fetchRecoveryRollbackRecommendation,
  fetchRecoveryRolloutBlockers,
  fetchRecoveryRolloutChecklist,
  fetchRecoveryRolloutReadiness,
  fetchRecoveryRolloutTransitionExamples,
  fetchRecoveryShadowSummary,
  validateRecoveryTransition,
} from '../lib/recoveryRolloutApi';
import { buildRiskMetrics, getNextRolloutMode, safeArray } from '../lib/recoveryRolloutFormatters';
import { recoveryRolloutQueryKeys } from '../lib/recoveryRolloutQueryKeys';

export const useRecoveryRolloutReadiness = (targetMode = null) =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.readiness(targetMode || 'next'),
    queryFn: () => fetchRecoveryRolloutReadiness(targetMode),
    refetchInterval: 15000,
  });

export const useRecoveryRolloutChecklist = (checklistType = 'pre', targetMode = null) =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.checklist(checklistType, targetMode || 'next'),
    queryFn: () => fetchRecoveryRolloutChecklist({ checklistType, targetMode }),
    refetchInterval: 20000,
  });

export const useRecoveryRolloutBlockers = (targetMode) =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.blockers(targetMode || 'V2_PRIMARY_WITH_LEGACY_FALLBACK'),
    queryFn: () => fetchRecoveryRolloutBlockers(targetMode || 'V2_PRIMARY_WITH_LEGACY_FALLBACK'),
    enabled: Boolean(targetMode),
    refetchInterval: 20000,
  });

export const useRecoveryRollbackRecommendation = () =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.rollbackRecommendation,
    queryFn: fetchRecoveryRollbackRecommendation,
    refetchInterval: 15000,
  });

export const useRecoveryControlCenterOverview = () =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.controlCenterOverview,
    queryFn: fetchRecoveryControlCenterOverview,
    refetchInterval: 20000,
  });

export const useRecoveryShadowSummary = () =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.shadowSummary,
    queryFn: fetchRecoveryShadowSummary,
    refetchInterval: 20000,
  });

export const useRecoveryMismatchSummary = () =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.controlCenterMismatches,
    queryFn: fetchRecoveryControlCenterMismatches,
    refetchInterval: 20000,
  });

export const useRecoveryControlCenterPerformance = () =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.controlCenterPerformance,
    queryFn: fetchRecoveryControlCenterPerformance,
    refetchInterval: 20000,
  });

export const useRecoveryTransitionExamples = () =>
  useQuery({
    queryKey: recoveryRolloutQueryKeys.transitionExamples,
    queryFn: fetchRecoveryRolloutTransitionExamples,
    refetchInterval: 60000,
  });

export const useValidateRecoveryTransition = () =>
  useMutation({
    mutationFn: validateRecoveryTransition,
  });

export const useChangeRecoveryRolloutMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeRecoveryRolloutMode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recovery-rollout'] });
      queryClient.invalidateQueries({ queryKey: ['recovery-control-center'] });
      queryClient.invalidateQueries({ queryKey: ['customer-monitoring'] });
      queryClient.invalidateQueries({ queryKey: ['monitoring'] });
    },
  });
};

export const useRecoveryRolloutData = () => {
  const [targetMode, setTargetMode] = useState(null);
  const [checklistType, setChecklistType] = useState('pre');
  const [validationResult, setValidationResult] = useState(null);
  const [modeHistory, setModeHistory] = useState([]);

  const readinessQuery = useRecoveryRolloutReadiness(targetMode);
  const resolvedCurrentMode = readinessQuery.data?.currentMode || 'SHADOW_V2';
  const resolvedTargetMode = targetMode || readinessQuery.data?.targetMode || getNextRolloutMode(resolvedCurrentMode);

  const checklistQuery = useRecoveryRolloutChecklist(checklistType, checklistType === 'pre' ? resolvedTargetMode : null);
  const blockersQuery = useRecoveryRolloutBlockers(resolvedTargetMode);
  const rollbackQuery = useRecoveryRollbackRecommendation();
  const overviewQuery = useRecoveryControlCenterOverview();
  const shadowSummaryQuery = useRecoveryShadowSummary();
  const mismatchQuery = useRecoveryMismatchSummary();
  const performanceQuery = useRecoveryControlCenterPerformance();
  const examplesQuery = useRecoveryTransitionExamples();

  const validateMutation = useValidateRecoveryTransition();
  const changeModeMutation = useChangeRecoveryRolloutMode();

  const riskMetrics = useMemo(
    () =>
      buildRiskMetrics({
        readiness: readinessQuery.data,
        shadowSummary: shadowSummaryQuery.data,
        performance: performanceQuery.data,
        mismatches: mismatchQuery.data,
      }),
    [readinessQuery.data, shadowSummaryQuery.data, performanceQuery.data, mismatchQuery.data],
  );

  const mismatchRows = useMemo(() => {
    const data = mismatchQuery.data || {};
    const mismatchByCategory = data.mismatch_by_category || {};
    return Object.entries(mismatchByCategory).map(([category, count]) => ({
      category,
      count: Number(count || 0),
    }));
  }, [mismatchQuery.data]);

  const isLoading = [
    readinessQuery,
    checklistQuery,
    blockersQuery,
    rollbackQuery,
    overviewQuery,
    shadowSummaryQuery,
    mismatchQuery,
    performanceQuery,
    examplesQuery,
  ].some((q) => q.isLoading);

  const error =
    readinessQuery.error ||
    checklistQuery.error ||
    blockersQuery.error ||
    rollbackQuery.error ||
    overviewQuery.error ||
    shadowSummaryQuery.error ||
    mismatchQuery.error ||
    performanceQuery.error ||
    examplesQuery.error ||
    validateMutation.error ||
    changeModeMutation.error;

  const refreshAll = () => {
    readinessQuery.refetch();
    checklistQuery.refetch();
    blockersQuery.refetch();
    rollbackQuery.refetch();
    overviewQuery.refetch();
    shadowSummaryQuery.refetch();
    mismatchQuery.refetch();
    performanceQuery.refetch();
    examplesQuery.refetch();
  };

  const runValidation = async (requestedBy = 'OPERATOR') => {
    const payload = {
      targetMode: resolvedTargetMode,
      requestedBy,
      allowEmergency: resolvedTargetMode === 'LEGACY_ONLY',
    };
    const result = await validateMutation.mutateAsync(payload);
    setValidationResult(result);
    return result;
  };

  const runModeChange = async (requestedBy = 'OPERATOR') => {
    const payload = {
      targetMode: resolvedTargetMode,
      requestedBy,
      allowEmergency: resolvedTargetMode === 'LEGACY_ONLY',
    };
    const result = await changeModeMutation.mutateAsync(payload);
    setModeHistory((prev) => [
      {
        id: `hist-${Date.now()}`,
        timestamp: new Date().toISOString(),
        previousMode: result?.previousMode || resolvedCurrentMode,
        targetMode: result?.newMode || resolvedTargetMode,
        allowed: Boolean(result?.changed),
        requestedBy,
        note: result?.message || 'Mode change attempted',
      },
      ...safeArray(prev),
    ].slice(0, 30));
    setValidationResult(null);
    refreshAll();
    return result;
  };

  return {
    currentMode: resolvedCurrentMode,
    targetMode: resolvedTargetMode,
    setTargetMode,
    checklistType,
    setChecklistType,
    readiness: readinessQuery.data || null,
    checklist: checklistQuery.data || { items: [] },
    blockers: blockersQuery.data || { blockers: [], warnings: [] },
    rollbackRecommendation: rollbackQuery.data || null,
    overview: overviewQuery.data || {},
    shadowSummary: shadowSummaryQuery.data || {},
    mismatchSummary: mismatchQuery.data || {},
    mismatchRows,
    performance: performanceQuery.data || {},
    riskMetrics,
    transitionExamples: safeArray(examplesQuery.data?.items),
    validationResult,
    modeHistory,
    runValidation,
    runModeChange,
    validateMutation,
    changeModeMutation,
    refreshAll,
    isLoading,
    error,
  };
};
